import { memo } from 'react'
import type { PointerEvent } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { PlusIcon } from '@heroicons/react/16/solid'
import { onlyControlsText, poolTarget } from '../../../Services/OrderWizard/issues.ts'
import type { PoolContents } from '../../../Services/OrderWizard/pools.ts'
import { itemCounts, lacksControl, poolTitle } from '../../../Services/OrderWizard/pools.ts'
import { shownName } from '../../../Services/OrderWizard/poolBoard.ts'
import { poolCopy } from '../../../Services/OrderWizard/wizardSettings.ts'
import { zoneFrameClass } from './zoneFrame.ts'
import { InlineWarning, NEW_POOL } from './Primitives.tsx'
import type { Attention, Layout, ListRowProps, RowProps } from './PoolRows.tsx'
import { ListRow, Row } from './PoolRows.tsx'
import type { DropData } from './usePoolDrag.ts'

/** A pool's header, alike in the list and on the board: its counts (with its name on the board) and what it lacks. */
const PoolHeader = ({
  layout,
  label,
  contents,
  lacking,
  onlyControls,
  empty
}: {
  layout: Layout
  label?: string
  contents: PoolContents | undefined
  /** How the missing negative control is put, when the pool has none. */
  lacking: string | undefined
  /** How a pool of controls only is put, when it is one. */
  onlyControls?: string
  empty?: boolean
}) => (
  <div className="border-b border-slate-200 px-2 py-1.5">
    <p className="font-medium text-slate-800" data-testid={layout === 'board' ? 'wizard-board-column-header' : 'wizard-pool-header'}>
      {label && `${label} · `}
      {itemCounts(contents?.samples ?? 0, contents?.controls.length ?? 0)}
    </p>
    {lacking && (
      <InlineWarning layout="inline" data-testid={`wizard-${layout}-missing-control`}>
        {lacking}
      </InlineWarning>
    )}
    {onlyControls && (
      <InlineWarning layout="inline" data-testid={`wizard-${layout}-only-controls`}>
        {onlyControls}
      </InlineWarning>
    )}
    {empty && <p className="text-xs text-slate-500">Empty: removed when you use these pools</p>}
  </div>
)

type ColumnProps = {
  pool: number
  number: number
  several: boolean
  /** The only column: it takes the board's width. */
  wide: boolean
  contents: PoolContents | undefined
  /** Whether the column is a pool as the draft has it, rather than one of unapplied changes. */
  committed: boolean
  /** The sample sheet's name for the pool, shown instead of its number. */
  name?: string
  names: Record<string, string>
  titles: Record<string, string>
  tags: Record<string, string>
  prefix: string
  controls: ReadonlySet<string>
  autoControls: ReadonlySet<string>
  attention: Readonly<Record<string, Attention>>
  selected: ReadonlySet<string>
  dragged: ReadonlySet<string>
  dragging: boolean
  onPress: (key: string) => void
} & Pick<RowProps, 'onClickItem' | 'onCheck' | 'onSetType'>

const columnFrame = (dragging: boolean, over: boolean, idle = 'border-slate-200 bg-white') =>
  `rounded-lg ${zoneFrameClass(over, dragging ? 'border-dashed border-slate-300 bg-slate-50' : idle)}`

export const PoolColumn = memo(function PoolColumn({
  pool,
  number,
  several,
  wide,
  contents,
  committed,
  name,
  names,
  titles,
  tags,
  prefix,
  controls,
  autoControls,
  attention,
  selected,
  dragged,
  dragging,
  onPress,
  onClickItem,
  onCheck,
  onSetType
}: ColumnProps) {
  const label = `pool ${number}`
  const drop = useDroppable({ id: `pool-${pool}`, data: { target: pool, label } satisfies DropData })
  const { setNodeRef, listeners, attributes } = useDraggable({ id: `column-${pool}` })
  const keys = contents?.keys ?? []
  const missing = !!contents && lacksControl(contents)
  const pointerDown = (event: PointerEvent) => {
    const target = event.target as Element
    const key = target.closest('[data-item]')?.getAttribute('data-item')
    if (!key || target.closest('input, button')) {
      return
    }
    onPress(key)
    listeners?.onPointerDown?.(event)
  }
  return (
    <div
      ref={drop.setNodeRef}
      id={committed ? poolTarget(pool) : undefined}
      className={`flex min-w-[220px] flex-col ${wide ? 'flex-1' : 'w-64 flex-none'} ${columnFrame(dragging, drop.isOver)}`}
      data-testid="wizard-board-column"
      data-pool={pool}
      data-over={drop.isOver ? 'true' : undefined}
      data-missing-control={missing ? 'true' : undefined}
    >
      <PoolHeader
        layout="board"
        label={poolTitle({ number, name })}
        contents={contents}
        lacking={missing ? poolCopy().withoutControl(several ? label : 'the pool', false) : undefined}
        onlyControls={several && contents && contents.samples === 0 && contents.controls.length > 0 ? onlyControlsText([pool]) : undefined}
        empty={keys.length === 0}
      />
      <ul
        ref={setNodeRef}
        className="min-h-0 flex-1 overflow-y-auto p-1 text-xs"
        aria-label={`Items in pool ${number}`}
        /* the list keeps its role and focus order; the selection bar is the keyboard path, which the instructions name */
        aria-roledescription={attributes['aria-roledescription']}
        aria-describedby={attributes['aria-describedby']}
        onPointerDown={pointerDown}
      >
        {keys.map((key) => (
          <Row
            key={key}
            groupKey={key}
            name={names[key]}
            shown={shownName(names[key], prefix)}
            title={titles[key]}
            tag={tags[key]}
            control={controls.has(key)}
            autoControl={autoControls.has(key)}
            compact={!wide}
            attention={attention[key]}
            selected={selected.has(key)}
            dimmed={dragged.has(key)}
            onClickItem={onClickItem}
            onCheck={onCheck}
            onSetType={onSetType}
          />
        ))}
      </ul>
    </div>
  )
})

type ItemListProps = Pick<
  ColumnProps,
  'pool' | 'contents' | 'names' | 'titles' | 'tags' | 'controls' | 'autoControls' | 'attention' | 'selected'
> &
  Pick<ListRowProps, 'onSetType' | 'onRemove' | 'onCheck'>

/** Every item in one list, for an order that is one pool: no columns, nothing to drag; checkboxes select for the bar. */
export const ItemList = ({
  pool,
  contents,
  names,
  titles,
  tags,
  controls,
  autoControls,
  attention,
  selected,
  onSetType,
  onCheck,
  onRemove
}: ItemListProps) => {
  const missing = !!contents && lacksControl(contents)
  return (
    <div
      id={poolTarget(pool)}
      className="rounded-lg border border-slate-200"
      data-testid="wizard-pool-list"
      data-missing-control={missing ? 'true' : undefined}
    >
      <PoolHeader layout="pool" contents={contents} lacking={missing ? poolCopy().withoutControl('the pool', false) : undefined} />
      <ul className="max-h-[50vh] overflow-y-auto p-1 text-xs" aria-label="Items">
        {(contents?.keys ?? []).map((key) => (
          <ListRow
            key={key}
            groupKey={key}
            name={names[key]}
            title={titles[key]}
            tag={tags[key]}
            control={controls.has(key)}
            autoControl={autoControls.has(key)}
            attention={attention[key]}
            selected={selected.has(key)}
            onSetType={onSetType}
            onCheck={onCheck}
            onRemove={onRemove}
          />
        ))}
      </ul>
    </div>
  )
}

export const NewPoolColumn = ({ number, dragging }: { number: number; dragging: boolean }) => {
  const drop = useDroppable({ id: NEW_POOL, data: { target: 'new', label: 'a new pool' } satisfies DropData })
  return (
    <div
      ref={drop.setNodeRef}
      className={`flex w-32 shrink-0 flex-col items-center justify-center gap-0.5 p-2 text-center ${
        drop.isOver ? 'motion-safe:w-56' : ''
      } ${columnFrame(dragging, drop.isOver, 'border-dashed border-slate-300 bg-white')}`}
      data-testid="wizard-board-new-pool"
      data-over={drop.isOver ? 'true' : undefined}
    >
      <PlusIcon className={`h-4 w-4 ${drop.isOver ? 'text-primary-600' : 'text-slate-400'}`} aria-hidden />
      <span className="text-sm font-semibold text-slate-700">New pool</span>
      <span className="text-xs text-slate-500">drop items here for pool {number}</span>
    </div>
  )
}
